<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, vue/html-closing-bracket-newline, vue/max-attributes-per-line */
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useRouter } from 'vue-router';
import { debounce } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import TalkviaSmsAPI from 'dashboard/api/talkviaSms';
import { subDays } from 'date-fns';
import {
  analyzeSmsMessage,
  MAX_SMS_SEGMENTS,
} from 'dashboard/helper/smsMessageAnalyzer';
import {
  analyzeSmsRecipients,
  phonesFromSmsCsv,
} from 'dashboard/helper/smsRecipientParser';

const router = useRouter();
const { isAdmin } = useAdmin();
const mode = ref(null);
const currentStep = ref(1);
const recipient = ref('');
const batchRecipients = ref('');
const importedFileName = ref('');
const fileInput = ref(null);
const message = ref('');
const payload = ref({ campaigns: [] });
const loading = ref(false);
const sending = ref(false);
const excludedLabels = ref([]);
const labelSearch = ref('');
const retryDialog = ref(null);
const retryTarget = ref(null);
const retrying = ref(false);
const availableLabels = computed(() =>
  (payload.value.labels || []).filter(label =>
    label.toLowerCase().includes(labelSearch.value.toLowerCase())
  )
);
const retryDescription = computed(() => {
  if (!retryTarget.value) return '';
  const { campaign, item } = retryTarget.value;
  const count = item ? 1 : campaign.retryable_count;
  return `Reenviar ${count} destinatário(s) com erro HTTP 500? Será criado um novo disparo com a mesma mensagem e exclusões por etiquetas. A nova tentativa reserva créditos; o disparo original permanece no histórico.`;
});
const acceptedGuidelines = ref(false);
const detailLoading = ref(false);
const selectedCampaign = ref(null);
const expandedMessages = ref([]);
const collapsedDateGroups = ref([]);
const refreshing = ref(false);
const lastUpdatedAt = ref(null);
const autoRefreshPaused = ref(false);
const searchQuery = ref('');
const statusFilter = ref('');
const historyDateRange = ref([subDays(new Date(), 29), new Date()]);
const historyRangeType = ref(DATE_RANGE_TYPES.LAST_30_DAYS);
const currentPage = ref(1);
const perPage = ref(20);
const historySection = ref(null);
const visibleRecipientPhones = ref([]);
const AUTO_REFRESH_INTERVAL = 10000;
let refreshTimer;
let resettingHistoryFilters = false;

const campaigns = computed(() => payload.value.campaigns || []);
const historyTotals = computed(() => payload.value.totals || {});
const formatTotal = value => Number(value || 0).toLocaleString('pt-BR');
const pagination = computed(() => payload.value.pagination || {});
const totalCampaigns = computed(() =>
  Number(pagination.value.total_count || 0)
);
const hasHistoryFilters = computed(
  () =>
    Boolean(searchQuery.value.trim()) ||
    Boolean(statusFilter.value) ||
    historyDateRange.value.every(Boolean)
);
const statusOptions = [
  { value: '', label: 'Todos os status' },
  { value: 'queued', label: 'Na fila' },
  { value: 'sending', label: 'Enviando' },
  { value: 'submitted', label: 'Processando' },
  { value: 'delivered', label: 'Entregue' },
  { value: 'partially_delivered', label: 'Parcial' },
  { value: 'failed', label: 'Não entregue' },
];
const perPageOptions = [
  { value: 10, label: '10 por página' },
  { value: 20, label: '20 por página' },
  { value: 50, label: '50 por página' },
];
const configured = computed(() => payload.value.configured === true);
const maxBatchSize = computed(() =>
  Number(payload.value.max_batch_size || 1000)
);
const balance = computed(() => Number(payload.value.balance || 0));
const hasNoCredits = computed(
  () =>
    Boolean(payload.value.product) &&
    Number.isFinite(Number(payload.value.balance)) &&
    Number(payload.value.balance) <= 0
);
const creditsPerUse = computed(() =>
  Number(payload.value.product?.credits_per_use || 1)
);
const maxSegments = computed(() =>
  Number(payload.value.max_message_segments || MAX_SMS_SEGMENTS)
);
const lastUpdatedLabel = computed(() =>
  lastUpdatedAt.value
    ? new Intl.DateTimeFormat('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }).format(lastUpdatedAt.value)
    : ''
);

const dateKey = value =>
  new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(value));

const recipientStats = computed(() => {
  let values = [];
  if (mode.value === 'single') values = [recipient.value];
  if (mode.value === 'batch') values = batchRecipients.value.split(/\r?\n/);
  return analyzeSmsRecipients(values);
});
const recipientCount = computed(() => recipientStats.value.valid.length);
const messageAnalysis = computed(() => analyzeSmsMessage(message.value));
const messageSegments = computed(() =>
  message.value.trim() ? messageAnalysis.value.segments : 0
);
const estimatedCredits = computed(
  () => recipientCount.value * creditsPerUse.value * messageSegments.value
);
const insufficientCredits = computed(
  () => estimatedCredits.value > balance.value
);
const forbiddenTerms = computed(() => {
  const terms = [];
  if (/\bpix\b/i.test(message.value)) terms.push('Pix');
  if (/(?<!\d)0800(?!\d)/.test(message.value)) terms.push('0800');
  return terms;
});
const hasPublicShortener = computed(() =>
  /(?:bit\.ly|tinyurl\.com)/i.test(message.value)
);
const recipientsReady = computed(
  () =>
    recipientCount.value > 0 &&
    recipientCount.value <= maxBatchSize.value &&
    recipientStats.value.invalid === 0
);
const messageReady = computed(
  () =>
    message.value.trim().length > 0 &&
    messageAnalysis.value.segments <= maxSegments.value &&
    forbiddenTerms.value.length === 0 &&
    (!insufficientCredits.value || excludedLabels.value.length > 0)
);
const modeLabel = computed(() =>
  mode.value === 'batch' ? 'Envio em lote' : 'Envio individual'
);
const canSend = computed(
  () =>
    configured.value &&
    mode.value &&
    acceptedGuidelines.value &&
    recipientsReady.value &&
    messageReady.value &&
    !sending.value
);

const maskPhone = value => {
  const digits = value
    .replace(/\D/g, '')
    .replace(/^55(?=\d{10,11}$)/, '')
    .slice(0, 11);
  return digits.length > 10
    ? digits.replace(/(\d{2})(\d{5})(\d{1,4})/, '($1) $2-$3')
    : digits.replace(/(\d{2})(\d{4})(\d{1,4})/, '($1) $2-$3');
};

const hydrateSmsDraft = () => {
  try {
    const key = `talkvia:sms:prefill:${router.currentRoute.value.params.accountId}`;
    const draft = JSON.parse(window.sessionStorage.getItem(key) || 'null');
    window.sessionStorage.removeItem(key);
    if (!draft?.phone) return;

    mode.value = 'single';
    currentStep.value = 2;
    recipient.value = maskPhone(String(draft.phone));
    message.value = String(draft.message || '');
  } catch {
    // Mantém o formulário vazio quando o navegador bloqueia o armazenamento.
  }
};

const updateRecipient = event => {
  recipient.value = maskPhone(event.target.value);
};

const updateBatch = event => {
  batchRecipients.value = event.target.value;
  importedFileName.value = '';
};

const importCsv = async event => {
  const file = event.target?.files?.[0] || event.dataTransfer?.files?.[0];
  if (!file) return;
  if (!file.name.toLowerCase().endsWith('.csv')) {
    useAlert('Selecione um arquivo no formato CSV.');
    return;
  }
  if (file.size > 2 * 1024 * 1024) {
    useAlert('O arquivo CSV deve ter no máximo 2 MB.');
    return;
  }

  const phones = phonesFromSmsCsv(await file.text());
  if (!phones.length) {
    useAlert('Nenhum telefone foi encontrado no arquivo CSV.');
    return;
  }
  batchRecipients.value = phones.join('\n');
  importedFileName.value = file.name;
};

const clearBatch = () => {
  batchRecipients.value = '';
  importedFileName.value = '';
  if (fileInput.value) fileInput.value.value = '';
};

const selectMode = nextMode => {
  if (mode.value && mode.value !== nextMode) {
    recipient.value = '';
    clearBatch();
  }
  mode.value = nextMode;
};

const nextStep = () => {
  if (currentStep.value === 1 && mode.value) currentStep.value = 2;
  else if (currentStep.value === 2 && recipientsReady.value) {
    currentStep.value = 3;
  }
};

const previousStep = () => {
  currentStep.value = Math.max(1, currentStep.value - 1);
};

const goToStep = step => {
  if (step <= currentStep.value) currentStep.value = step;
};

const openMarketplace = () => router.push({ name: 'talkvia_shop' });

const load = async ({ silent = false } = {}) => {
  if (refreshing.value) return;

  refreshing.value = true;
  if (!silent) loading.value = true;
  try {
    const { data } = await TalkviaSmsAPI.list({
      page: currentPage.value,
      per_page: perPage.value,
      q: searchQuery.value.trim() || undefined,
      status: statusFilter.value || undefined,
      date_from: historyDateRange.value[0]
        ? dateKey(historyDateRange.value[0])
        : undefined,
      date_to: historyDateRange.value[1]
        ? dateKey(historyDateRange.value[1])
        : undefined,
    });
    payload.value = data;
    currentPage.value = Number(data.pagination?.page || 1);

    if (
      selectedCampaign.value &&
      campaigns.value.some(
        campaign => campaign.public_id === selectedCampaign.value.public_id
      )
    ) {
      const details = await TalkviaSmsAPI.details(
        selectedCampaign.value.public_id
      );
      selectedCampaign.value = details.data;
    } else if (selectedCampaign.value) {
      selectedCampaign.value = null;
    }
    lastUpdatedAt.value = new Date();
  } catch (error) {
    if (!silent) {
      useAlert(
        error.response?.data?.error ||
          'Não foi possível carregar os disparos SMS.'
      );
    }
  } finally {
    refreshing.value = false;
    if (!silent) loading.value = false;
  }
};

const refreshWhenVisible = () => {
  if (!autoRefreshPaused.value && document.visibilityState === 'visible') {
    load({ silent: true });
  }
};

const toggleAutoRefresh = () => {
  autoRefreshPaused.value = !autoRefreshPaused.value;
  if (!autoRefreshPaused.value) load({ silent: true });
};

const resetOpenCampaigns = () => {
  selectedCampaign.value = null;
  expandedMessages.value = [];
  visibleRecipientPhones.value = [];
};

const applyFilters = () => {
  currentPage.value = 1;
  resetOpenCampaigns();
  load();
};

const debouncedSearch = debounce(applyFilters, 350);

watch(searchQuery, () => {
  if (!resettingHistoryFilters) debouncedSearch();
});
watch(statusFilter, () => {
  if (!resettingHistoryFilters) applyFilters();
});
watch(perPage, () => {
  if (!resettingHistoryFilters) applyFilters();
});

const onHistoryDateRangeChanged = ([startDate, endDate, selectedRange]) => {
  if (!startDate || !endDate) return;

  historyDateRange.value = [startDate, endDate];
  historyRangeType.value = selectedRange;
  applyFilters();
};

const goToHistoryPage = async page => {
  if (page === currentPage.value) return;

  currentPage.value = page;
  resetOpenCampaigns();
  await load();
  historySection.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const clearHistoryFilters = async () => {
  resettingHistoryFilters = true;
  debouncedSearch.cancel?.();
  searchQuery.value = '';
  statusFilter.value = '';
  historyDateRange.value = [subDays(new Date(), 29), new Date()];
  historyRangeType.value = DATE_RANGE_TYPES.LAST_30_DAYS;
  await nextTick();
  resettingHistoryFilters = false;
  applyFilters();
};

const send = async () => {
  const recipients =
    mode.value === 'single' ? recipient.value : batchRecipients.value;
  if (!recipients.trim() || !message.value.trim()) {
    useAlert('Informe os destinatários e a mensagem.');
    return;
  }
  if (recipientCount.value > maxBatchSize.value) {
    useAlert(`O limite é de ${maxBatchSize.value} destinatários por disparo.`);
    return;
  }
  if (recipientStats.value.invalid) {
    useAlert('Revise os telefones inválidos antes de enviar.');
    return;
  }
  if (messageAnalysis.value.segments > maxSegments.value) {
    useAlert(
      `A mensagem pode ter no máximo ${maxSegments.value} segmentos SMS.`
    );
    return;
  }
  if (forbiddenTerms.value.length) {
    useAlert(
      `Remova os termos bloqueados: ${forbiddenTerms.value.join(', ')}.`
    );
    return;
  }
  if (insufficientCredits.value && !excludedLabels.value.length) {
    useAlert('Saldo de créditos insuficiente para este disparo.');
    return;
  }
  if (!acceptedGuidelines.value) {
    useAlert('Confirme que revisou as orientações antes de enviar.');
    return;
  }

  sending.value = true;
  try {
    const { data } = await TalkviaSmsAPI.create(
      recipientStats.value.valid,
      message.value,
      excludedLabels.value
    );
    recipient.value = '';
    batchRecipients.value = '';
    importedFileName.value = '';
    message.value = '';
    acceptedGuidelines.value = false;
    mode.value = null;
    currentStep.value = 1;
    currentPage.value = 1;
    useAlert(
      `Disparo enfileirado: ${data.total_recipients} destinatário(s). ${data.excluded_recipients_count || 0} excluído(s) por etiquetas.`,
      { variant: 'positive' }
    );
    await load();
  } catch (error) {
    useAlert(
      error.response?.data?.error || 'Não foi possível enfileirar o disparo.'
    );
  } finally {
    sending.value = false;
  }
};

const openRetry = (campaign, item = null) => {
  retryTarget.value = { campaign, item };
  retryDialog.value.open();
};

const confirmRetry = async () => {
  if (retrying.value || !retryTarget.value) return;
  retrying.value = true;
  try {
    const { campaign, item } = retryTarget.value;
    const { data } = await TalkviaSmsAPI.retryFailed(
      campaign.public_id,
      item?.public_id
    );
    retryDialog.value.close();
    useAlert(
      `Reenvio enfileirado para ${data.total_recipients} destinatário(s).`,
      { variant: 'positive' }
    );
    await load();
  } catch (error) {
    useAlert(error.response?.data?.error || 'Não foi possível reenviar o SMS.');
  } finally {
    retrying.value = false;
  }
};

const exportCampaign = async campaign => {
  try {
    const { data } = await TalkviaSmsAPI.export(campaign.public_id);
    const url = URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.download = `sms-${campaign.public_id}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  } catch {
    useAlert('Não foi possível exportar o relatório.');
  }
};

const toggleCampaignDetails = async campaign => {
  if (selectedCampaign.value?.public_id === campaign.public_id) {
    selectedCampaign.value = null;
    visibleRecipientPhones.value = [];
    return;
  }

  detailLoading.value = true;
  try {
    const { data } = await TalkviaSmsAPI.details(campaign.public_id);
    selectedCampaign.value = data;
    visibleRecipientPhones.value = [];
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        'Não foi possível carregar os detalhes do disparo.'
    );
  } finally {
    detailLoading.value = false;
  }
};

const maskStoredPhone = value => {
  const digits = String(value || '').replace(/\D/g, '');
  return digits.length >= 4 ? '(**) *****-' + digits.slice(-4) : '—';
};

const formatStoredPhone = value => {
  const formatted = maskPhone(String(value || ''));
  return formatted ? `+55 ${formatted}` : '—';
};

const isRecipientPhoneVisible = item =>
  visibleRecipientPhones.value.includes(item.public_id);

const toggleRecipientPhone = item => {
  visibleRecipientPhones.value = isRecipientPhoneVisible(item)
    ? visibleRecipientPhones.value.filter(id => id !== item.public_id)
    : [...visibleRecipientPhones.value, item.public_id];
};

const formatDateGroup = value => {
  const date = new Date(value);
  const today = dateKey(new Date());
  const yesterdayDate = new Date();
  yesterdayDate.setDate(yesterdayDate.getDate() - 1);
  const key = dateKey(date);
  let prefix = '';
  if (key === today) prefix = 'Hoje · ';
  else if (key === dateKey(yesterdayDate)) prefix = 'Ontem · ';
  const label = new Intl.DateTimeFormat('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(date);
  return `${prefix}${label.charAt(0).toUpperCase()}${label.slice(1)}`;
};

const formatTime = value =>
  new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));

const campaignCountLabel = count => (count === 1 ? 'disparo' : 'disparos');

const isDateGroupCollapsed = groupKey =>
  collapsedDateGroups.value.includes(groupKey);

const toggleDateGroup = groupKey => {
  collapsedDateGroups.value = isDateGroupCollapsed(groupKey)
    ? collapsedDateGroups.value.filter(key => key !== groupKey)
    : [...collapsedDateGroups.value, groupKey];
};

const groupedCampaigns = computed(() => {
  const groups = new Map();
  campaigns.value.forEach(campaign => {
    const key = dateKey(campaign.created_at);
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        label: formatDateGroup(campaign.created_at),
        campaigns: [],
      });
    }
    groups.get(key).campaigns.push(campaign);
  });
  return [...groups.values()];
});

const statusLabel = status =>
  ({
    queued: 'Na fila',
    sending: 'Enviando',
    submitted: 'Processando',
    delivered: 'Entregue',
    partially_delivered: 'Parcial',
    failed: 'Não entregue',
    invalid: 'Rejeitado',
  })[status] || status;

const statusTone = status =>
  ({
    queued: 'border-n-slate-5 bg-n-slate-3 text-n-slate-11',
    sending: 'border-n-blue-7 bg-n-blue-3 text-n-blue-11',
    submitted: 'border-n-blue-7 bg-n-blue-3 text-n-blue-11',
    delivered: 'border-n-teal-7 bg-n-teal-3 text-n-teal-11',
    partially_delivered: 'border-n-amber-7 bg-n-amber-3 text-n-amber-11',
    failed: 'border-n-ruby-7 bg-n-ruby-3 text-n-ruby-11',
    invalid: 'border-n-ruby-7 bg-n-ruby-3 text-n-ruby-11',
  })[status] || 'border-n-slate-5 bg-n-slate-3 text-n-slate-11';

const isMessageExpanded = campaign =>
  expandedMessages.value.includes(campaign.public_id);

const toggleMessage = campaign => {
  expandedMessages.value = isMessageExpanded(campaign)
    ? expandedMessages.value.filter(id => id !== campaign.public_id)
    : [...expandedMessages.value, campaign.public_id];
};

onMounted(() => {
  hydrateSmsDraft();
  load();
  document.addEventListener('visibilitychange', refreshWhenVisible);
  refreshTimer = window.setInterval(refreshWhenVisible, AUTO_REFRESH_INTERVAL);
});

onBeforeUnmount(() => {
  document.removeEventListener('visibilitychange', refreshWhenVisible);
  window.clearInterval(refreshTimer);
  debouncedSearch.cancel?.();
});
</script>

<template>
  <main class="h-full min-h-0 w-full overflow-y-auto bg-n-surface-1">
    <div
      class="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-6 pb-24 sm:px-6 sm:py-8"
    >
      <header class="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p
            class="mb-1 text-xs font-semibold uppercase tracking-wide text-n-brand"
          >
            Comunicação
          </p>
          <h1 class="mb-2 text-2xl font-semibold text-n-slate-12 sm:text-3xl">
            Disparos SMS
          </h1>
          <p class="mb-0 max-w-3xl text-sm text-n-slate-11 sm:text-base">
            Envie pela Kolmeya para um telefone ou importe uma lista completa de
            destinatários.
          </p>
        </div>
        <div
          class="rounded-xl border border-n-weak bg-n-solid-2 px-4 py-3 shadow-sm"
        >
          <p class="m-0 text-xs text-n-slate-10">Saldo disponível</p>
          <p class="m-0 text-2xl font-semibold text-n-slate-12">
            {{ balance }} créditos
          </p>
          <p class="m-0 mt-1 text-xs text-n-slate-10">
            {{ creditsPerUse }} crédito por segmento e destinatário
          </p>
        </div>
      </header>

      <section
        v-if="!configured"
        class="rounded-xl border border-n-amber-5 bg-n-amber-2 p-4 text-sm text-n-amber-12"
      >
        A integração aguarda a configuração segura das credenciais Kolmeya no
        servidor.
      </section>

      <section
        aria-labelledby="sms-guidelines-title"
        class="overflow-hidden rounded-2xl border border-n-amber-5 bg-n-solid-2 shadow-sm"
      >
        <div
          class="flex items-start gap-3 border-b border-n-amber-5 bg-n-amber-2 px-4 py-4 sm:px-6"
        >
          <span
            class="i-lucide-shield-alert mt-0.5 size-6 shrink-0 text-n-amber-11"
          />
          <div>
            <h2
              id="sms-guidelines-title"
              class="m-0 text-base font-semibold text-n-amber-12"
            >
              Antes de enviar
            </h2>
            <p class="m-0 mt-1 text-sm text-n-amber-11">
              A operadora pode bloquear a entrega por conteúdo e ainda
              contabilizar o envio. Revise a mensagem antes de confirmar.
            </p>
          </div>
        </div>
        <div class="grid gap-px bg-n-weak md:grid-cols-3">
          <article class="bg-n-solid-2 p-4 sm:p-5">
            <div class="mb-2 flex items-center gap-2">
              <span class="i-lucide-ban size-4 text-n-ruby-10" />
              <h3 class="m-0 text-sm font-semibold text-n-slate-12">
                Conteúdo bloqueado
              </h3>
            </div>
            <p class="m-0 text-sm leading-6 text-n-slate-11">
              Não envie conteúdo ofensivo, fraudulento ou de baixa qualidade. Os
              termos “Pix” e “0800” não são permitidos.
            </p>
          </article>
          <article class="bg-n-solid-2 p-4 sm:p-5">
            <div class="mb-2 flex items-center gap-2">
              <span class="i-lucide-link size-4 text-n-blue-10" />
              <h3 class="m-0 text-sm font-semibold text-n-slate-12">
                Links confiáveis
              </h3>
            </div>
            <p class="m-0 text-sm leading-6 text-n-slate-11">
              Para WhatsApp, use <strong>wa.me/55DDDNUMERO</strong>. Evite
              bit.ly e tinyurl, que podem prejudicar a entrega.
            </p>
          </article>
          <article class="bg-n-solid-2 p-4 sm:p-5">
            <div class="mb-2 flex items-center gap-2">
              <span class="i-lucide-building-2 size-4 text-n-teal-10" />
              <h3 class="m-0 text-sm font-semibold text-n-slate-12">
                Identifique a empresa
              </h3>
            </div>
            <p class="m-0 text-sm leading-6 text-n-slate-11">
              Informe a empresa remetente. Ao direcionar para WhatsApp, mantenha
              o perfil Business completo e coerente.
            </p>
          </article>
        </div>
      </section>

      <section
        class="rounded-2xl border border-n-weak bg-n-solid-2 p-4 shadow-sm sm:p-6"
      >
        <div
          class="mb-5 flex flex-col gap-3 border-b border-n-weak pb-5 sm:flex-row sm:items-start sm:justify-between"
        >
          <div>
            <h2 class="m-0 text-lg font-semibold text-n-slate-12">
              Novo disparo
            </h2>
            <p class="m-0 mt-1 text-sm text-n-slate-10">
              Destinatários duplicados são consolidados antes da cobrança.
            </p>
          </div>
          <div
            v-if="!hasNoCredits && currentStep === 3"
            class="rounded-xl border border-n-weak bg-n-alpha-2 px-4 py-3 text-sm text-n-slate-11"
          >
            <p class="m-0 text-xs">Custo estimado</p>
            <p class="m-0 mt-0.5 text-lg font-semibold text-n-slate-12">
              {{ estimatedCredits }} créditos
            </p>
            <p class="m-0 mt-0.5 text-xs text-n-slate-10">
              {{ recipientCount }} destinatário(s) ×
              {{ messageSegments }} segmento(s)
            </p>
          </div>
          <div
            v-else-if="!hasNoCredits"
            class="rounded-full border border-n-weak bg-n-alpha-2 px-3 py-1.5 text-xs font-medium text-n-slate-11"
          >
            Etapa {{ currentStep }} de 3
          </div>
          <div
            v-else
            class="inline-flex items-center gap-2 rounded-full border border-n-amber-5 bg-n-amber-3 px-3 py-1.5 text-xs font-semibold text-n-amber-11"
          >
            <span class="i-lucide-circle-alert size-3.5" />
            Saldo esgotado
          </div>
        </div>

        <nav
          v-if="!hasNoCredits"
          aria-label="Etapas do disparo"
          class="mb-6 grid grid-cols-3 overflow-hidden rounded-xl border border-n-weak bg-n-alpha-1"
        >
          <button
            v-for="step in [
              { number: 1, label: 'Tipo de envio' },
              { number: 2, label: 'Destinatários' },
              { number: 3, label: 'Mensagem e revisão' },
            ]"
            :key="step.number"
            type="button"
            class="flex min-w-0 items-center justify-center gap-2 border-r border-n-weak px-2 py-3 text-left text-xs transition last:border-r-0 sm:px-4 sm:text-sm"
            :class="
              currentStep === step.number
                ? 'bg-n-teal-3 font-semibold text-n-teal-11'
                : currentStep > step.number
                  ? 'cursor-pointer bg-n-alpha-2 text-n-slate-12 hover:bg-n-alpha-3'
                  : 'cursor-default text-n-slate-9'
            "
            :disabled="currentStep < step.number"
            @click="goToStep(step.number)"
          >
            <span
              class="flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold"
              :class="
                currentStep === step.number
                  ? 'bg-n-teal-9 text-white'
                  : currentStep > step.number
                    ? 'bg-n-teal-3 text-n-teal-11'
                    : 'bg-n-alpha-3 text-n-slate-9'
              "
            >
              <span
                v-if="currentStep > step.number"
                class="i-lucide-check size-3.5"
              />
              <span v-else>{{ step.number }}</span>
            </span>
            <span class="truncate">{{ step.label }}</span>
          </button>
        </nav>

        <div
          v-if="hasNoCredits"
          class="mx-auto flex w-full max-w-2xl flex-col items-center px-4 py-8 text-center sm:py-12"
        >
          <div
            class="mb-5 flex size-16 items-center justify-center rounded-2xl border border-n-amber-5 bg-n-amber-3 text-n-amber-11 shadow-sm"
          >
            <span class="i-lucide-wallet-cards size-8" />
          </div>
          <p
            class="mb-3 rounded-full bg-n-amber-3 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-n-amber-11"
          >
            Créditos necessários
          </p>
          <h3 class="m-0 text-xl font-semibold text-n-slate-12">
            Você está sem créditos para novos disparos
          </h3>
          <p class="m-0 mt-2 max-w-xl text-sm leading-6 text-n-slate-11">
            <template v-if="isAdmin">
              Adquira créditos no Marketplace Talkvia para continuar enviando
              mensagens SMS.
            </template>
            <template v-else>
              Solicite ao administrador da conta a compra de créditos para
              liberar novos envios.
            </template>
          </p>
          <Button
            v-if="isAdmin"
            label="Comprar créditos no Marketplace"
            icon="i-lucide-shopping-cart"
            class="mt-6 w-full sm:w-auto"
            @click="openMarketplace"
          />
        </div>

        <div v-else-if="currentStep === 1" class="mx-auto w-full max-w-4xl">
          <div class="mb-4 text-center">
            <h3 class="m-0 text-base font-semibold text-n-slate-12">
              Como deseja enviar?
            </h3>
            <p class="m-0 mt-1 text-sm text-n-slate-10">
              Escolha uma opção para começar o novo disparo.
            </p>
          </div>
          <div class="mb-6 grid gap-3 sm:grid-cols-2">
            <button
              type="button"
              class="flex items-center gap-3 rounded-xl border p-4 text-left transition"
              :class="
                mode === 'single'
                  ? 'border-n-brand bg-n-brand/5 ring-1 ring-n-brand'
                  : 'border-n-weak bg-n-alpha-1 hover:border-n-strong'
              "
              @click="selectMode('single')"
            >
              <span
                class="i-lucide-smartphone size-5 shrink-0"
                :class="mode === 'single' ? 'text-n-brand' : 'text-n-slate-10'"
              />
              <span>
                <strong class="block text-sm text-n-slate-12"
                  >Envio individual</strong
                >
                <span class="text-xs text-n-slate-10"
                  >Digite um telefone com DDD</span
                >
              </span>
            </button>
            <button
              type="button"
              class="flex items-center gap-3 rounded-xl border p-4 text-left transition"
              :class="
                mode === 'batch'
                  ? 'border-n-brand bg-n-brand/5 ring-1 ring-n-brand'
                  : 'border-n-weak bg-n-alpha-1 hover:border-n-strong'
              "
              @click="selectMode('batch')"
            >
              <span
                class="i-lucide-file-spreadsheet size-5 shrink-0"
                :class="mode === 'batch' ? 'text-n-brand' : 'text-n-slate-10'"
              />
              <span>
                <strong class="block text-sm text-n-slate-12"
                  >Envio em lote</strong
                >
                <span class="text-xs text-n-slate-10"
                  >Cole números ou importe um CSV</span
                >
              </span>
            </button>
          </div>
          <div class="flex justify-end border-t border-n-weak pt-5">
            <Button
              label="Continuar para destinatários"
              icon="i-lucide-arrow-right"
              class="w-full sm:w-auto"
              :disabled="!mode"
              @click="nextStep"
            />
          </div>
        </div>

        <div v-if="!hasNoCredits" class="grid gap-5">
          <div
            v-if="currentStep === 2"
            class="mx-auto flex w-full max-w-3xl flex-col gap-4"
          >
            <div class="flex items-center gap-2">
              <span
                class="flex size-7 items-center justify-center rounded-full bg-n-teal-3 text-xs font-semibold text-n-teal-11"
                >2</span
              >
              <div>
                <h3 class="m-0 text-sm font-semibold text-n-slate-12">
                  Destinatários
                </h3>
                <p class="m-0 text-xs text-n-slate-10">
                  Telefones brasileiros com DDD; o código +55 é opcional.
                </p>
              </div>
            </div>

            <label
              v-if="mode === 'single'"
              class="flex flex-col gap-2 text-sm font-medium text-n-slate-12"
            >
              Telefone
              <div
                class="flex h-12 overflow-hidden rounded-lg border border-n-weak bg-n-alpha-1 focus-within:border-n-brand"
              >
                <span
                  class="flex items-center border-r border-n-weak bg-n-alpha-2 px-3 font-normal text-n-slate-10"
                  >BR +55</span
                >
                <input
                  class="min-w-0 flex-1 bg-transparent px-3 font-normal outline-none"
                  :value="recipient"
                  placeholder="(00) 00000-0000"
                  inputmode="numeric"
                  @input="updateRecipient"
                />
              </div>
              <span
                v-if="recipientStats.total && recipientStats.invalid"
                class="text-xs font-normal text-n-ruby-11"
              >
                Informe um telefone válido com DDD.
              </span>
            </label>

            <div v-else class="flex flex-col gap-3">
              <label
                class="flex flex-col gap-2 text-sm font-medium text-n-slate-12"
              >
                Colar telefones
                <textarea
                  class="min-h-44 resize-y rounded-lg border border-n-weak bg-n-alpha-1 p-3 font-mono text-sm font-normal outline-none transition focus:border-n-brand"
                  :value="batchRecipients"
                  placeholder="5519999999999&#10;19988887777&#10;+55 (19) 97777-6666"
                  @input="updateBatch"
                />
                <span class="text-xs font-normal text-n-slate-10">
                  Um número por linha. Pode usar somente dígitos ou números
                  completos com +55.
                </span>
              </label>

              <div
                class="rounded-xl border border-dashed border-n-strong bg-n-alpha-1 p-4"
                @dragover.prevent
                @drop.prevent="importCsv"
              >
                <input
                  ref="fileInput"
                  type="file"
                  accept=".csv,text/csv"
                  class="hidden"
                  @change="importCsv"
                />
                <div class="flex flex-wrap items-center justify-between gap-3">
                  <div class="flex items-center gap-3">
                    <span class="i-lucide-file-up size-5 text-n-slate-10" />
                    <div>
                      <p class="m-0 text-sm font-medium text-n-slate-12">
                        {{ importedFileName || 'Importar arquivo CSV' }}
                      </p>
                      <p class="m-0 text-xs text-n-slate-10">
                        Coluna “telefone”, “celular” ou a primeira coluna · até
                        2 MB
                      </p>
                    </div>
                  </div>
                  <div class="flex gap-2">
                    <Button
                      v-if="batchRecipients"
                      label="Limpar"
                      color="slate"
                      variant="ghost"
                      size="sm"
                      @click="clearBatch"
                    />
                    <Button
                      label="Escolher CSV"
                      icon="i-lucide-upload"
                      color="slate"
                      variant="outline"
                      size="sm"
                      @click="fileInput?.click()"
                    />
                  </div>
                </div>
              </div>

              <div class="grid grid-cols-3 gap-2 text-center text-xs">
                <div class="rounded-lg bg-n-alpha-2 p-2 text-n-slate-11">
                  <strong class="block text-base text-n-slate-12">{{
                    recipientCount
                  }}</strong>
                  válidos
                </div>
                <div class="rounded-lg bg-n-alpha-2 p-2 text-n-slate-11">
                  <strong class="block text-base text-n-amber-11">{{
                    recipientStats.duplicates
                  }}</strong>
                  duplicados
                </div>
                <div
                  class="rounded-lg p-2"
                  :class="
                    recipientStats.invalid
                      ? 'bg-n-ruby-2 text-n-ruby-11'
                      : 'bg-n-alpha-2 text-n-slate-11'
                  "
                >
                  <strong class="block text-base">{{
                    recipientStats.invalid
                  }}</strong>
                  inválidos
                </div>
              </div>
              <p
                class="m-0 text-xs"
                :class="
                  recipientCount > maxBatchSize
                    ? 'text-n-ruby-11'
                    : 'text-n-slate-10'
                "
              >
                Limite: {{ recipientCount }} de {{ maxBatchSize }} números
                únicos.
              </p>
            </div>
            <fieldset class="rounded-xl border border-n-weak p-4">
              <legend class="px-1 text-sm font-medium text-n-slate-12">
                Não enviar para contatos com estas etiquetas
              </legend>
              <p class="text-xs text-n-slate-11">
                Qualquer etiqueta selecionada no contato ou nas conversas dele
                nesta conta bloqueia o envio, inclusive em lotes. Excluídos não
                consomem créditos.
              </p>
              <Input
                v-model="labelSearch"
                label="Buscar etiqueta"
                placeholder="Digite o nome da etiqueta"
                type="search"
              />
              <div class="mt-3 flex max-h-40 flex-wrap gap-3 overflow-y-auto">
                <label
                  v-for="label in availableLabels"
                  :key="label"
                  class="flex items-center gap-2 text-sm text-n-slate-12"
                >
                  <input
                    v-model="excludedLabels"
                    type="checkbox"
                    :value="label"
                    class="rounded border-n-strong text-n-brand"
                  />
                  {{ label }}
                </label>
                <span
                  v-if="!availableLabels.length"
                  class="text-xs text-n-slate-11"
                  >Nenhuma etiqueta encontrada.</span
                >
              </div>
              <p
                v-if="excludedLabels.length"
                class="mt-3 text-xs text-n-slate-11"
              >
                Selecionadas: {{ excludedLabels.join(', ') }}. O custo estimado
                será reduzido após a exclusão no servidor.
              </p>
            </fieldset>
          </div>

          <div
            v-if="currentStep === 3"
            class="mx-auto flex w-full max-w-4xl flex-col gap-3"
          >
            <div class="flex items-center gap-2">
              <span
                class="flex size-7 items-center justify-center rounded-full bg-n-blue-3 text-xs font-semibold text-n-blue-11"
                >3</span
              >
              <div>
                <h3 class="m-0 text-sm font-semibold text-n-slate-12">
                  Mensagem e revisão
                </h3>
                <p class="m-0 text-xs text-n-slate-10">
                  O custo é calculado por segmento SMS.
                </p>
              </div>
            </div>
            <textarea
              v-model="message"
              class="min-h-44 resize-y rounded-lg border border-n-weak bg-n-alpha-1 p-3 text-sm font-normal outline-none transition focus:border-n-brand"
              placeholder="Ex.: Talkvia: seu atendimento foi atualizado."
            />
            <div class="grid grid-cols-3 gap-2 text-center text-xs">
              <div class="rounded-lg bg-n-alpha-2 p-2 text-n-slate-11">
                <strong class="block text-sm text-n-slate-12">
                  {{
                    messageAnalysis.encoding === 'gsm7' ? 'GSM-7' : 'Unicode'
                  }}
                </strong>
                codificação
              </div>
              <div class="rounded-lg bg-n-alpha-2 p-2 text-n-slate-11">
                <strong class="block text-sm text-n-slate-12">{{
                  messageAnalysis.units
                }}</strong>
                unidades
              </div>
              <div
                class="rounded-lg p-2"
                :class="
                  messageAnalysis.segments > maxSegments
                    ? 'bg-n-ruby-2 text-n-ruby-11'
                    : 'bg-n-alpha-2 text-n-slate-11'
                "
              >
                <strong class="block text-sm"
                  >{{ messageSegments }}/{{ maxSegments }}</strong
                >
                segmentos
              </div>
            </div>
            <div class="h-1.5 overflow-hidden rounded-full bg-n-alpha-3">
              <div
                class="h-full rounded-full transition-all"
                :class="
                  messageAnalysis.segments > maxSegments
                    ? 'bg-n-ruby-9'
                    : 'bg-n-brand'
                "
                :style="{
                  width: `${Math.min(100, (messageAnalysis.units / (messageAnalysis.segmentSize * Math.max(messageAnalysis.segments, 1))) * 100)}%`,
                }"
              />
            </div>
            <div
              class="flex items-start justify-between gap-3 text-xs font-normal"
            >
              <span
                v-if="forbiddenTerms.length"
                class="font-medium text-n-ruby-11"
              >
                Remova: {{ forbiddenTerms.join(', ') }}
              </span>
              <span
                v-else-if="hasPublicShortener"
                class="font-medium text-n-amber-11"
              >
                Evite encurtadores públicos
              </span>
              <span v-else class="text-n-slate-10"
                >Identifique a empresa remetente</span
              >
              <span v-if="message" class="shrink-0 text-n-slate-10">
                {{ messageAnalysis.remaining }} unidade(s) disponíveis neste
                segmento
              </span>
            </div>
            <div
              class="flex items-start gap-2 rounded-lg bg-n-blue-2 p-3 text-xs leading-5 text-n-blue-11"
            >
              <span class="i-lucide-info mt-0.5 size-4 shrink-0" />
              Caracteres fora do padrão GSM-7 — como “á”, “ç” e emojis — mudam a
              mensagem para Unicode: 70 unidades no primeiro segmento e 67 nos
              seguintes. Caracteres GSM estendidos, como chaves, ocupam 2
              unidades.
            </div>

            <div class="mt-2 rounded-xl border border-n-teal-5 bg-n-teal-2 p-4">
              <div class="mb-3 flex items-center gap-2">
                <span class="i-lucide-clipboard-check size-4 text-n-teal-11" />
                <h4 class="m-0 text-sm font-semibold text-n-teal-12">
                  Revise o disparo
                </h4>
              </div>
              <dl class="m-0 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <dt class="text-xs text-n-slate-10">Tipo</dt>
                  <dd class="m-0 mt-0.5 text-sm font-medium text-n-slate-12">
                    {{ modeLabel }}
                  </dd>
                </div>
                <div>
                  <dt class="text-xs text-n-slate-10">Destinatários</dt>
                  <dd class="m-0 mt-0.5 text-sm font-medium text-n-slate-12">
                    {{ recipientCount }}
                  </dd>
                </div>
                <div>
                  <dt class="text-xs text-n-slate-10">
                    Segmentos por destinatário
                  </dt>
                  <dd class="m-0 mt-0.5 text-sm font-medium text-n-slate-12">
                    {{ messageSegments }}
                  </dd>
                </div>
                <div>
                  <dt class="text-xs text-n-slate-10">Custo estimado</dt>
                  <dd class="m-0 mt-0.5 text-sm font-medium text-n-slate-12">
                    {{ estimatedCredits }} créditos
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </div>

        <div
          v-if="currentStep === 2"
          class="mt-6 flex flex-col-reverse gap-3 border-t border-n-weak pt-5 sm:flex-row sm:justify-between"
        >
          <Button
            label="Voltar"
            icon="i-lucide-arrow-left"
            color="slate"
            variant="ghost"
            class="w-full sm:w-auto"
            @click="previousStep"
          />
          <Button
            label="Continuar para mensagem"
            icon="i-lucide-arrow-right"
            class="w-full sm:w-auto"
            :disabled="!recipientsReady"
            @click="nextStep"
          />
        </div>

        <div
          v-if="currentStep === 3 && insufficientCredits"
          class="mt-5 flex items-start gap-2 rounded-lg border border-n-ruby-5 bg-n-ruby-2 p-3 text-sm text-n-ruby-11"
        >
          <span class="i-lucide-wallet-cards mt-0.5 size-4 shrink-0" />
          {{
            excludedLabels.length
              ? 'O saldo será conferido após excluir os contatos pelas etiquetas selecionadas.'
              : 'O saldo atual não cobre o custo estimado deste disparo.'
          }}
        </div>

        <div
          v-if="currentStep === 3"
          class="mt-5 flex flex-col gap-4 border-t border-n-weak pt-5 sm:flex-row sm:items-center sm:justify-between"
        >
          <Button
            label="Voltar"
            icon="i-lucide-arrow-left"
            color="slate"
            variant="ghost"
            class="w-full shrink-0 sm:w-auto"
            @click="previousStep"
          />
          <label
            class="flex max-w-3xl flex-1 cursor-pointer items-start gap-3 text-sm text-n-slate-11"
          >
            <input
              v-model="acceptedGuidelines"
              type="checkbox"
              class="mt-0.5 size-4 shrink-0 rounded border-n-strong text-n-brand focus:ring-n-brand"
            />
            <span>
              Confirmo que revisei o conteúdo, possuo autorização para contatar
              os destinatários e compreendo as regras de entrega e consumo de
              créditos.
            </span>
          </label>
          <Button
            label="Enviar SMS"
            icon="i-lucide-send"
            class="w-full shrink-0 sm:w-auto"
            :is-loading="sending"
            :disabled="!canSend"
            @click="send"
          />
        </div>
      </section>

      <section
        ref="historySection"
        class="scroll-mt-4 overflow-hidden rounded-2xl border border-n-weak bg-n-solid-2 shadow-sm"
      >
        <div
          class="flex flex-wrap items-start justify-between gap-4 p-4 sm:p-6"
        >
          <div>
            <h2 class="m-0 text-lg font-semibold text-n-slate-12">Histórico</h2>
            <p class="m-0 text-sm text-n-slate-10">
              Consulte os disparos sem carregar todo o histórico de uma vez.
            </p>
          </div>
          <div class="flex flex-wrap items-center justify-end gap-2">
            <div
              class="flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium"
              :class="
                autoRefreshPaused
                  ? 'border-n-amber-5 bg-n-amber-3 text-n-amber-11'
                  : 'border-n-teal-5 bg-n-teal-3 text-n-teal-11'
              "
              :title="
                autoRefreshPaused
                  ? 'A atualização automática está pausada'
                  : 'O histórico é sincronizado automaticamente a cada 10 segundos'
              "
            >
              <span class="relative flex size-2">
                <span
                  v-if="!autoRefreshPaused"
                  class="absolute inline-flex size-full animate-ping rounded-full bg-n-teal-9 opacity-50"
                />
                <span
                  class="relative inline-flex size-2 rounded-full"
                  :class="autoRefreshPaused ? 'bg-n-amber-9' : 'bg-n-teal-9'"
                />
              </span>
              {{
                autoRefreshPaused
                  ? 'Atualização pausada'
                  : 'Atualização automática'
              }}
              <span v-if="lastUpdatedLabel" class="opacity-80">
                · {{ lastUpdatedLabel }}
              </span>
            </div>
            <Button
              :label="autoRefreshPaused ? 'Retomar' : 'Pausar'"
              :icon="autoRefreshPaused ? 'i-lucide-play' : 'i-lucide-pause'"
              color="slate"
              variant="outline"
              size="sm"
              @click="toggleAutoRefresh"
            />
          </div>
        </div>

        <div class="border-y border-n-weak bg-n-alpha-1 p-4 sm:px-6 sm:py-5">
          <div
            class="rounded-xl border border-n-weak bg-n-solid-1 p-4 shadow-sm"
          >
            <div
              class="mb-4 flex flex-wrap items-start justify-between gap-3 border-b border-n-weak pb-4"
            >
              <div class="flex min-w-0 items-start gap-3">
                <span
                  class="grid size-9 shrink-0 place-items-center rounded-lg bg-n-brand/10 text-n-brand"
                >
                  <span class="i-lucide-list-filter size-4" />
                </span>
                <div class="min-w-0">
                  <h3 class="m-0 text-sm font-semibold text-n-slate-12">
                    Filtros do histórico
                  </h3>
                  <p class="m-0 mt-0.5 text-xs text-n-slate-10">
                    Encontre disparos por conteúdo, período ou situação.
                  </p>
                </div>
              </div>
              <Button
                v-if="hasHistoryFilters"
                label="Limpar filtros"
                icon="i-lucide-rotate-ccw"
                color="slate"
                variant="ghost"
                size="sm"
                @click="clearHistoryFilters"
              />
            </div>

            <div class="grid gap-4 sm:grid-cols-2 xl:grid-cols-12 xl:items-end">
              <Input
                v-model="searchQuery"
                label="Buscar"
                placeholder="Mensagem ou telefone"
                type="search"
                class="min-w-0 sm:col-span-2 xl:col-span-4"
              />
              <label
                class="flex min-w-0 flex-col gap-1.5 text-sm font-medium text-n-slate-12 xl:col-span-4"
              >
                Período do envio
                <WootDatePicker
                  v-model:date-range="historyDateRange"
                  v-model:range-type="historyRangeType"
                  teleport-to-body
                  class="min-w-0 [&>div]:!flex [&>div]:!w-full [&>div]:min-w-0 [&_button]:h-10 [&_button]:min-w-0 [&_button]:max-w-full [&_button]:flex-1 [&_button]:justify-start [&_button]:overflow-hidden [&_button]:border [&_button]:border-n-weak [&_button]:bg-n-solid-2 [&_button_span]:min-w-0 [&_button_span]:truncate"
                  @date-range-changed="onHistoryDateRangeChanged"
                />
              </label>
              <label
                class="flex min-w-0 flex-col gap-1.5 text-sm font-medium text-n-slate-12 xl:col-span-2"
              >
                Status
                <Select
                  v-model="statusFilter"
                  :options="statusOptions"
                  class="w-full [&>select]:h-10 [&>select]:w-full"
                />
              </label>
              <label
                class="flex min-w-0 flex-col gap-1.5 text-sm font-medium text-n-slate-12 xl:col-span-2"
              >
                Itens por página
                <Select
                  v-model="perPage"
                  :options="perPageOptions"
                  class="w-full [&>select]:h-10 [&>select]:w-full"
                />
              </label>
            </div>
          </div>
        </div>

        <div
          class="flex flex-wrap items-center justify-between gap-2 border-b border-n-weak px-4 py-3 text-xs text-n-slate-10 sm:px-6"
        >
          <span>
            {{ totalCampaigns }}
            {{
              totalCampaigns === 1
                ? 'disparo encontrado'
                : 'disparos encontrados'
            }}
          </span>
          <span v-if="hasHistoryFilters">Filtros aplicados</span>
        </div>

        <dl
          class="grid grid-cols-1 gap-3 border-b border-n-weak px-4 py-4 sm:grid-cols-2 sm:px-6"
          aria-label="Totais do histórico filtrado"
          :aria-busy="refreshing"
        >
          <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
            <dt class="text-sm text-n-slate-11">Total de SMS</dt>
            <dd
              class="mt-1 text-2xl font-semibold tabular-nums text-n-slate-12"
            >
              {{ loading ? '—' : formatTotal(historyTotals.total) }}
            </dd>
            <p class="mt-1 text-xs text-n-slate-10">
              Destinatários de todos os disparos encontrados
            </p>
          </div>
          <div class="rounded-xl border border-n-teal-7 bg-n-teal-3 p-4">
            <dt class="text-sm text-n-teal-11">Total entregue</dt>
            <dd class="mt-1 text-2xl font-semibold tabular-nums text-n-teal-11">
              {{ loading ? '—' : formatTotal(historyTotals.delivered) }}
            </dd>
            <p class="mt-1 text-xs text-n-teal-11">
              SMS com entrega confirmada pela operadora
            </p>
          </div>
        </dl>

        <div
          v-if="loading"
          class="flex items-center justify-center gap-2 py-16 text-sm text-n-slate-10"
        >
          <span class="i-lucide-loader-circle size-5 animate-spin" />
          Carregando histórico…
        </div>

        <div v-else-if="campaigns.length" class="space-y-6 p-4 sm:p-6">
          <section
            v-for="group in groupedCampaigns"
            :key="group.key"
            class="overflow-hidden rounded-xl border border-n-weak bg-n-solid-1 shadow-sm"
          >
            <button
              type="button"
              class="flex w-full items-center justify-between gap-3 border-0 bg-n-alpha-1 px-4 py-3 text-left transition-colors hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand focus-visible:outline-offset-[-2px]"
              :aria-expanded="!isDateGroupCollapsed(group.key)"
              :aria-controls="`sms-date-group-${group.key}`"
              @click="toggleDateGroup(group.key)"
            >
              <span class="flex min-w-0 items-center gap-3">
                <span
                  class="grid size-8 shrink-0 place-items-center rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-10"
                >
                  <span
                    class="i-lucide-chevron-down size-4 transition-transform duration-200"
                    :class="{
                      '-rotate-90': isDateGroupCollapsed(group.key),
                    }"
                  />
                </span>
                <span class="min-w-0">
                  <span
                    class="block truncate text-sm font-semibold text-n-slate-12"
                  >
                    {{ group.label }}
                  </span>
                  <span class="block text-xs text-n-slate-10 sm:hidden">
                    {{ group.campaigns.length }}
                    {{ campaignCountLabel(group.campaigns.length) }}
                  </span>
                </span>
              </span>
              <span class="flex shrink-0 items-center gap-3">
                <span
                  class="hidden rounded-full border border-n-weak bg-n-solid-2 px-2.5 py-1 text-xs font-medium text-n-slate-10 sm:inline-flex"
                >
                  {{ group.campaigns.length }}
                  {{ campaignCountLabel(group.campaigns.length) }}
                </span>
                <span
                  class="hidden text-xs font-medium text-n-slate-11 sm:inline"
                >
                  {{
                    isDateGroupCollapsed(group.key) ? 'Expandir' : 'Recolher'
                  }}
                </span>
              </span>
            </button>

            <div
              v-show="!isDateGroupCollapsed(group.key)"
              :id="`sms-date-group-${group.key}`"
              class="divide-y divide-n-weak border-t border-n-weak"
            >
              <article
                v-for="campaign in group.campaigns"
                :key="campaign.public_id"
                class="p-4 transition-colors hover:bg-n-alpha-1"
              >
                <div
                  class="grid gap-4 lg:grid-cols-[5rem_minmax(0,1fr)_minmax(11rem,15rem)_auto] lg:items-start"
                >
                  <div
                    class="flex items-center gap-2 lg:flex-col lg:items-start"
                  >
                    <span
                      class="text-base font-semibold tabular-nums text-n-slate-12"
                    >
                      {{ formatTime(campaign.created_at) }}
                    </span>
                    <span
                      class="rounded-md bg-n-alpha-2 px-2 py-1 text-[11px] font-medium text-n-slate-10"
                    >
                      {{ campaign.source === 'batch' ? 'Lote' : 'Individual' }}
                    </span>
                  </div>

                  <div class="min-w-0">
                    <p
                      class="m-0 text-sm leading-6 text-n-slate-12"
                      :class="
                        isMessageExpanded(campaign)
                          ? 'whitespace-pre-wrap break-words'
                          : 'line-clamp-2'
                      "
                    >
                      {{ campaign.message }}
                    </p>
                    <Button
                      :label="
                        isMessageExpanded(campaign)
                          ? 'Ocultar mensagem'
                          : 'Ver mensagem completa'
                      "
                      :icon="
                        isMessageExpanded(campaign)
                          ? 'i-lucide-eye-off'
                          : 'i-lucide-eye'
                      "
                      color="slate"
                      variant="ghost"
                      size="sm"
                      class="mt-1"
                      @click="toggleMessage(campaign)"
                    />
                  </div>

                  <div class="flex min-w-0 flex-col items-start gap-1.5">
                    <span
                      class="inline-flex rounded-full border px-2.5 py-1 text-xs font-medium"
                      :class="statusTone(campaign.status)"
                    >
                      {{ statusLabel(campaign.status) }}
                    </span>
                    <p
                      v-for="failure in campaign.failure_reasons"
                      :key="failure.reason"
                      class="m-0 break-words text-xs leading-5 text-n-ruby-11"
                    >
                      <strong>Motivo:</strong>
                      <span v-if="failure.count > 1">
                        {{ failure.count }} destinatários —
                      </span>
                      {{ failure.reason }}
                    </p>
                  </div>

                  <div class="flex flex-wrap gap-1 lg:justify-end">
                    <Button
                      v-if="campaign.retryable_count > 0"
                      :label="`Reenviar erros 500 (${campaign.retryable_count})`"
                      icon="i-lucide-rotate-cw"
                      variant="outline"
                      size="sm"
                      :disabled="retrying"
                      @click="openRetry(campaign)"
                    />
                    <Button
                      :label="
                        selectedCampaign?.public_id === campaign.public_id
                          ? 'Fechar'
                          : 'Detalhes'
                      "
                      icon="i-lucide-list"
                      color="slate"
                      variant="ghost"
                      size="sm"
                      :is-loading="
                        detailLoading &&
                        selectedCampaign?.public_id !== campaign.public_id
                      "
                      @click="toggleCampaignDetails(campaign)"
                    />
                    <Button
                      label="Exportar"
                      icon="i-lucide-download"
                      color="slate"
                      variant="ghost"
                      size="sm"
                      @click="exportCampaign(campaign)"
                    />
                  </div>
                </div>

                <div
                  class="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-n-weak pt-3 text-xs text-n-slate-10"
                >
                  <span class="inline-flex items-center gap-1.5">
                    <span class="i-lucide-users size-3.5" />
                    <strong class="text-n-slate-12">{{
                      campaign.total_recipients
                    }}</strong>
                    destinatários
                  </span>
                  <span class="inline-flex items-center gap-1.5">
                    <span
                      class="i-lucide-circle-check size-3.5 text-n-teal-10"
                    />
                    <strong class="text-n-slate-12">{{
                      campaign.counts?.delivered || 0
                    }}</strong>
                    entregues
                  </span>
                  <span class="inline-flex items-center gap-1.5">
                    <span class="i-lucide-user-round size-3.5" />
                    {{ campaign.agent }}
                  </span>
                  <span v-if="campaign.excluded_recipients_count"
                    >{{ campaign.excluded_recipients_count }} excluído(s) por
                    etiquetas</span
                  >
                  <span v-if="campaign.excluded_labels?.length"
                    >Etiquetas bloqueadas:
                    {{ campaign.excluded_labels.join(', ') }}</span
                  >
                </div>

                <div
                  v-if="selectedCampaign?.public_id === campaign.public_id"
                  class="mt-4 rounded-xl border border-n-weak bg-n-alpha-1 p-3 sm:p-4"
                >
                  <div class="mb-3 flex flex-wrap gap-2">
                    <span
                      v-for="(count, status) in selectedCampaign.counts"
                      :key="status"
                      class="rounded-lg border px-3 py-2 text-xs"
                      :class="statusTone(status)"
                    >
                      {{ statusLabel(status) }}:
                      <strong>{{ count }}</strong>
                    </span>
                  </div>
                  <div class="grid gap-2">
                    <div
                      v-for="item in selectedCampaign.recipients"
                      :key="item.public_id"
                      class="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2"
                    >
                      <div class="flex items-center gap-2">
                        <span class="font-medium text-n-slate-12">
                          {{
                            isRecipientPhoneVisible(item)
                              ? formatStoredPhone(item.phone)
                              : maskStoredPhone(item.phone)
                          }}
                        </span>
                        <button
                          type="button"
                          class="grid size-8 place-items-center rounded-lg text-n-slate-10 transition hover:bg-n-alpha-2 hover:text-n-slate-12"
                          :title="
                            isRecipientPhoneVisible(item)
                              ? 'Ocultar telefone'
                              : 'Ver telefone'
                          "
                          :aria-label="
                            isRecipientPhoneVisible(item)
                              ? 'Ocultar telefone do destinatário'
                              : 'Ver telefone do destinatário'
                          "
                          @click="toggleRecipientPhone(item)"
                        >
                          <span
                            class="size-4"
                            :class="
                              isRecipientPhoneVisible(item)
                                ? 'i-lucide-eye-off'
                                : 'i-lucide-eye'
                            "
                          />
                        </button>
                      </div>
                      <div
                        class="flex flex-wrap items-center justify-end gap-2"
                      >
                        <span
                          class="rounded-full border px-2.5 py-1 text-xs font-medium"
                          :class="statusTone(item.status)"
                        >
                          {{ statusLabel(item.status) }}
                        </span>
                        <span
                          v-if="item.error_message"
                          class="max-w-md text-xs leading-5 text-n-ruby-11"
                        >
                          <strong>Motivo:</strong> {{ item.error_message }}
                        </span>
                        <Button
                          v-if="item.retryable"
                          label="Reenviar"
                          icon="i-lucide-rotate-cw"
                          variant="outline"
                          size="sm"
                          :disabled="retrying"
                          @click="openRetry(campaign, item)"
                        />
                        <span
                          v-else-if="item.retried_campaign_id"
                          class="text-xs text-n-slate-11"
                          >Nova tentativa registrada</span
                        >
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            </div>
          </section>
        </div>

        <div v-else class="flex flex-col items-center px-4 py-16 text-center">
          <span
            class="i-lucide-message-square-search mb-3 size-8 text-n-slate-9"
          />
          <p class="m-0 text-sm font-medium text-n-slate-12">
            {{
              hasHistoryFilters
                ? 'Nenhum disparo encontrado'
                : 'Nenhum disparo realizado'
            }}
          </p>
          <p v-if="hasHistoryFilters" class="m-0 mt-1 text-xs text-n-slate-10">
            Ajuste a busca ou limpe os filtros para ver outros resultados.
          </p>
        </div>

        <PaginationFooter
          v-if="totalCampaigns > 0"
          :current-page="currentPage"
          :total-items="totalCampaigns"
          :items-per-page="perPage"
          @update:current-page="goToHistoryPage"
        />
      </section>
    </div>
    <Dialog
      ref="retryDialog"
      title="Confirmar reenvio de SMS"
      :description="retryDescription"
      confirm-button-label="Reenviar SMS"
      cancel-button-label="Cancelar"
      :is-loading="retrying"
      :disable-confirm-button="retrying"
      :close-on-click-outside="!retrying"
      @confirm="confirmRetry"
    />
  </main>
</template>
